import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LockKey, CheckCircle } from "@phosphor-icons/react";
import { resetPassword, formatApiErrorDetail } from "../lib/api";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div data-testid="reset-password-page" className="mx-auto flex min-h-[80vh] max-w-sm flex-col items-center justify-center px-4 py-16 text-center">
        <p data-testid="reset-password-missing-token" className="text-sm text-red-400">
          Missing or invalid reset link.{" "}
          <Link to="/forgot-password" className="text-eb-accent hover:underline">
            Request a new one
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div data-testid="reset-password-page" className="mx-auto flex min-h-[80vh] max-w-sm flex-col justify-center px-4 py-16">
      <div className="mb-8 text-center">
        <Link to="/" className="font-heading text-2xl font-black uppercase tracking-tighter text-white">
          EDGE<span className="text-eb-accent">BALL</span>
        </Link>
        <h1 className="mt-4 font-heading text-xl uppercase tracking-tight text-white">Set New Password</h1>
      </div>

      {done ? (
        <p data-testid="reset-password-success-message" className="flex items-center justify-center gap-2 rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-400">
          <CheckCircle size={16} weight="bold" /> Password updated. Redirecting to log in&hellip;
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 rounded-lg border border-white/10 bg-eb-surface p-6">
          {error && (
            <p data-testid="reset-password-error" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {error}
            </p>
          )}
          <input
            data-testid="reset-password-new-input"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New password (min 8 characters)"
            className="rounded-md border border-white/10 bg-eb-bg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-eb-accent focus:outline-none"
          />
          <input
            data-testid="reset-password-confirm-input"
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            className="rounded-md border border-white/10 bg-eb-bg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-eb-accent focus:outline-none"
          />
          <button
            data-testid="reset-password-submit-button"
            type="submit"
            disabled={submitting}
            className="mt-2 flex items-center justify-center gap-2 rounded-md bg-eb-accent px-4 py-2 font-heading text-sm uppercase tracking-wide text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
          >
            <LockKey size={16} weight="bold" /> {submitting ? "Updating\u2026" : "Update Password"}
          </button>
        </form>
      )}
    </div>
  );
};

export default ResetPassword;
